/**
 * Canlı kanal React bağı (TI-012/TI-013): kapsam (çalışma alanı + kullanıcı) başına tek kanal, abone sayısıyla açılır/kapanır.
 * Kapsam değişince (çıkış/başka çalışma alanı) eski kanal kapanır; yeni kapsamın kanalı ayrıdır.
 */
import { useEffect, useRef, useState } from 'react';
import { scopeKey, useScope } from '../../lib/scope';
import { LiveChannel, type LiveMessage, type LiveStatus } from './live-client';

let current: { key: string; ch: LiveChannel; refs: number } | null = null;
/** Test: kanal kurucusu değiştirilebilir */
let factory: () => LiveChannel = () => new LiveChannel();
export function setLiveChannelFactory(f: (() => LiveChannel) | null): void {
  current?.ch.stop();
  current = null;
  factory = f ?? (() => new LiveChannel());
}

function acquire(key: string): LiveChannel {
  if (current !== null && current.key !== key) {
    current.ch.stop();
    current = null;
  }
  if (current === null) {
    current = { key, ch: factory(), refs: 0 };
    current.ch.start();
  }
  current.refs += 1;
  return current.ch;
}
function release(ch: LiveChannel): void {
  if (current?.ch !== ch) return;
  current.refs -= 1;
  if (current.refs <= 0) {
    ch.stop();
    current = null;
  }
}

/**
 * Kanca dışı abonelik (aynı tek kanal): kapsam anahtarının kanalına abone olur, durum değişimlerini bildirir; dönen işlev
 * aboneliği kapatır ve kanalı bırakır (son abone gidince kanal kapanır). Gelen kutusu canlı kaynağı bunu kullanır (`inbox/live-sse.ts`).
 */
export function openLiveSubscription(
  key: string,
  onMessage: (m: LiveMessage) => void,
  onStatus?: (s: LiveStatus) => void,
): () => void {
  const ch = acquire(key);
  onStatus?.(ch.getStatus());
  const offStatus = onStatus === undefined ? () => undefined : ch.onStatus(onStatus);
  const off = ch.subscribe(onMessage);
  return () => {
    off();
    offStatus();
    release(ch);
  };
}

/** Canlı kanal olaylarına abone olur; işleyici her çizimde güncel tutulur (yeniden abonelik yok). */
export function useLive(handler: (m: LiveMessage) => void, enabled = true): LiveStatus {
  const scope = useScope();
  const key = scope === null ? null : scopeKey(scope);
  const ref = useRef(handler);
  ref.current = handler;
  const [status, setStatus] = useState<LiveStatus>('off');
  useEffect(() => {
    if (key === null || !enabled) return;
    return openLiveSubscription(
      key,
      (m) => {
        ref.current(m);
      },
      setStatus,
    );
  }, [key, enabled]);
  return status;
}
